import React, { createContext, useContext, useState, useCallback } from 'react';
import type { User, Project, Channel, Message, Task, Team, Agent } from '../api/types';

export interface AppState {
  // Auth
  user: User | null;
  // Projects
  projects: Project[];
  activeProjectId: string | null;
  // Channels
  channels: Channel[];
  activeChannelId: string | null;
  // Messages
  messages: Message[];
  // Tasks
  tasks: Task[];
  // Teams / Agents
  teams: Team[];
  agents: Agent[];
}

interface AppContextValue extends AppState {
  setUser: (user: User | null) => void;
  setProjects: (projects: Project[]) => void;
  setActiveProjectId: (id: string | null) => void;
  addProject: (project: Project) => void;
  setChannels: (channels: Channel[]) => void;
  setActiveChannelId: (id: string | null) => void;
  setMessages: (messages: Message[]) => void;
  appendMessage: (message: Message) => void;
  setTasks: (tasks: Task[]) => void;
  updateTask: (task: Task) => void;
  addTask: (task: Task) => void;
  setTeams: (teams: Team[]) => void;
  setAgents: (agents: Agent[]) => void;
  updateAgentStatus: (agentId: string, status: Agent['status']) => void;
}

const AppContext = createContext<AppContextValue | null>(null);

export const AppProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [projects, setProjects] = useState<Project[]>([]);
  const [activeProjectId, setActiveProjectId] = useState<string | null>(null);
  const [channels, setChannels] = useState<Channel[]>([]);
  const [activeChannelId, setActiveChannelId] = useState<string | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [agents, setAgents] = useState<Agent[]>([]);

  const addProject = useCallback((p: Project) => setProjects((prev) => [...prev, p]), []);

  const appendMessage = useCallback(
    (msg: Message) => setMessages((prev) => [...prev, msg]),
    [],
  );

  const updateTask = useCallback(
    (updated: Task) =>
      setTasks((prev) => prev.map((t) => (t.id === updated.id ? updated : t))),
    [],
  );

  const addTask = useCallback((t: Task) => setTasks((prev) => [...prev, t]), []);

  const updateAgentStatus = useCallback(
    (agentId: string, status: Agent['status']) =>
      setAgents((prev) =>
        prev.map((a) => (a.id === agentId ? { ...a, status } : a)),
      ),
    [],
  );

  return (
    <AppContext.Provider
      value={{
        user,
        projects,
        activeProjectId,
        channels,
        activeChannelId,
        messages,
        tasks,
        teams,
        agents,
        setUser,
        setProjects,
        setActiveProjectId,
        addProject,
        setChannels,
        setActiveChannelId,
        setMessages,
        appendMessage,
        setTasks,
        updateTask,
        addTask,
        setTeams,
        setAgents,
        updateAgentStatus,
      }}
    >
      {children}
    </AppContext.Provider>
  );
};

export function useAppContext(): AppContextValue {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useAppContext must be used within AppProvider');
  return ctx;
}
